// AppHeader.tsx
import {
  Link,
  useLocation,
  useRouteLoaderData,
} from "@remix-run/react";
import clsx from "clsx";
import React, {
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { loader } from "./../../root";
import { generateFileURL } from "~/utils/file/generateFileURL";
import Logo from "~/assets/ourpr_logo.svg";
import HeaderMenu from "./HeaderMenu";
import IconNotify from "../Icon/IconNotify";
import NotifyMemu from "./NotifyMemu";
import { AnimatePresence } from "framer-motion";
import IconArticle from "../Icon/IconArticle";

interface AppHeaderProps {}

const useMenu = () => {
  const [menuOpen, setMenuOpen] =
    React.useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent,
    ) => {
      if (
        menuOpen &&
        menuRef.current &&
        !menuRef.current.contains(
          event.target as Node,
        ) &&
        buttonRef.current &&
        !buttonRef.current.contains(
          event.target as Node,
        )
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener(
      "mousedown",
      handleClickOutside,
    );
    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );
    };
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  return {
    menuOpen,
    menuRef,
    buttonRef,
    toggleMenu,
    setMenuOpen,
  };
};
const AppHeader: React.FC<
  AppHeaderProps
> = () => {
  const root =
    useRouteLoaderData<typeof loader>("root");
  const userData = root?.userData;
  const notificationData = root?.notificationData;
  const notifyUnreadCount = useMemo(() => {
    return (
      notificationData?.filter(
        (el) => !el.is_read,
      ).length ?? 0
    );
  }, [notificationData]);
  const location = useLocation();
  const currentPath = location.pathname;

  const {
    menuOpen,
    menuRef,
    buttonRef,
    toggleMenu,
    setMenuOpen,
  } = useMenu();
  const {
    menuOpen: menuOpenNotify,
    menuRef: menuRefNotify,
    buttonRef: buttonRefNotify,
    toggleMenu: toggleMenuNotify,
    setMenuOpen: setMenuOpenNotify,
  } = useMenu();

  useEffect(() => {
    setMenuOpen(false);
    setMenuOpenNotify(false);
  }, [
    location.pathname,
    setMenuOpen,
    setMenuOpenNotify,
  ]);

  if (currentPath.indexOf("posts/edit") !== -1) {
    return null;
  }
  return (
    <div
      className={clsx(
        "fixed",
        "top-0",
        "left-0",
        "w-full",
        "z-20",
        "px-40",
        "text-sm",
        //"bg-white",
        "max-md:px-15",
      )}
    >
      <div
        className={clsx(
          "flex",
          "justify-between",
          "items-center",
          //   "border-b",
          //   "border-black",
          "py-14",
        )}
      >
        <div
          className={clsx(
            "w-90",
            "pt-0",
            "pointer-events-auto",
          )}
        >
          <Link to="/">
            <img src={Logo} alt="" />
          </Link>
        </div>
        {userData && (
          <div
            className={clsx(
              "flex",
              "gap-20",
              "items-center",
              "pointer-events-auto",
              "max-md:gap-15",
            )}
          >
            {!root?.subscription_active && (
              <div
                className={clsx(
                  "text-13",
                  "max-md:text-12",
                )}
              >
                <Link to={`/select-plan`}>
                  配信したい方はこちら
                </Link>
              </div>
            )}
            {root?.subscription_active && (
              <div
                className={clsx(
                  "w-24",
                  "max-md:w-20",
                )}
              >
                <Link to={"/dashboard"}>
                  <IconArticle></IconArticle>
                </Link>
              </div>
            )}

            <div className={clsx("relative")}>
              {menuOpenNotify && (
                <NotifyMemu
                  menuRef={menuRefNotify}
                />
              )}

              {notifyUnreadCount > 0 && (
                <div
                  className={clsx(
                    "absolute",
                    "-top-2",
                    "-right-6",
                    "w-20",
                    "h-20",
                    "text-12",
                    "leading-[1]",
                    "bg-red-500",
                    "text-white",
                    "z-20",
                    "flex",
                    "items-center",
                    "justify-center",
                    "rounded-full",
                  )}
                >
                  {notifyUnreadCount}
                </div>
              )}
              <div
                className={clsx(
                  "w-24",
                  "cursor-pointer",
                  "relative",
                  "max-md:w-20",
                )}
                ref={buttonRefNotify}
                onClick={() => {
                  toggleMenuNotify();
                }}
              >
                <IconNotify></IconNotify>
              </div>
            </div>

            <div
              className={clsx(
                "text-center",
                "relative",
              )}
            >
              <AnimatePresence>
                {menuOpen && (
                  <HeaderMenu menuRef={menuRef} />
                )}
              </AnimatePresence>
              <div
                className={clsx(
                  "w-43",
                  "aspect-square",
                  "relative",
                  "rounded-full",
                  "overflow-hidden",
                  "bg-gray-200",
                  "mx-auto",
                  "border-black",
                  "border",
                  "rounded-full",
                  "relative",
                  "cursor-pointer",
                  "max-md:w-38",
                )}
                ref={buttonRef}
                onClick={() => {
                  toggleMenu();
                }}
              >
                {userData.iconurl ? (
                  <img
                    className={clsx(
                      "h-full w-full object-cover",
                      "rounded-full",
                    )}
                    src={generateFileURL(
                      userData.iconurl,
                      undefined,
                      root?.basePath,
                    )}
                    alt=""
                  />
                ) : (
                  <div
                    className={clsx(
                      "h-full w-full object-cover",
                    )}
                  ></div>
                )}
              </div>
            </div>
          </div>
        )}

        {!userData && (
          <div
            className={clsx(
              "pointer-events-auto flex gap-20",
              "py-10",
            )}
          >
            <Link to="/login">ログイン</Link>
            <Link to="/create-account">登録</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppHeader;
